/**
 * The typed main-thread proxy for the kernel worker (Requirements 14.1, 14.7,
 * 16.2).
 *
 * Everything the UI knows about the Workbook comes through here. Beyond forwarding
 * messages the client owns four things:
 *
 * **The promise contract.** Each request carries a client-minted id and a pending
 * promise; the matching reply settles it. A rejected *command* is not an error
 * here — `dispatch` resolves with the same `{ ok: false, error }` value the
 * in-process dispatcher returns, so callers have one code path whether the
 * rejection came from the validator or from the boundary. Only a failure of the
 * *protocol* rejects, and it rejects with {@link KernelUnavailableError}, which
 * carries the envelope so the correlation id survives to the UI notice and the
 * diagnostics export.
 *
 * **The tile transport.** The client decides, because cross-origin isolation is a
 * property of the *page*: it asks for shared buffers when it can read them and for
 * transferable copies otherwise, and `decodeTile` produces the same
 * `TileSnapshot` either way, so the Grid_Renderer never learns which it got.
 *
 * **Crash recovery.** Every successful operation is appended to a
 * {@link CommandJournal}. If the worker dies — an `error` event, an uncloneable
 * message, or a heartbeat that stops arriving — the client buries it, starts a
 * fresh one, restores the newest snapshot, replays the journal since that
 * snapshot, and reports what came back through {@link KernelClient.onRecovered}.
 * In-flight requests reject rather than being reissued: a command that may or may
 * not have applied before the worker died cannot be retried safely, and only the
 * caller knows whether retrying is right.
 *
 * **A stall is a crash.** The design monitors the worker with a heartbeat, and a
 * worker wedged inside a pathological synchronous operation is indistinguishable
 * from a dead one from out here. Rather than guess, the client recovers both the
 * same way; the timeout is four beats so an ordinary long operation or a GC pause
 * never trips it.
 *
 * The client imports no runtime value from the computation core: the kernel belongs
 * in the worker chunk, and a stray value import here would pull it into the initial
 * load budget (Requirement 14.3).
 */

import type { ErrorEnvelope } from '../core/command/index.js';
import type { CommandResult, HistoryCommandResult } from '../core/command/index.js';
import type { Command } from '../core/command/index.js';
import type { ChartId, ClipPayload } from '../core/command/index.js';
import type { ChartSummary } from '../core/chart/model.js';
import type { AggResult, FindOptions } from '../core/query/index.js';
import type { TileSnapshot } from '../core/store/sheet.js';
import type { Addr, Direction, Range, SheetId } from '../core/types.js';
import type { CellContent, SheetView, ViewTile } from './view.js';
import {
  createMemoryJournal,
  createNullSnapshotStore,
  type CommandJournal,
  type JournalOp,
  type SnapshotStore,
} from './journal.js';
import {
  HEARTBEAT_TIMEOUT_MS,
  isReply,
  KERNEL_PROTOCOL_VERSION,
  type ChangedEvent,
  type ClientRequest,
  type ClientRequestKind,
  type KernelEvent,
  type ProgressEvent,
  type ReplayReport,
} from './protocol.js';
import { decodeTile, preferredTileTransport, type TileTransportMode } from './tile.js';

/**
 * The slice of `Worker` this client uses.
 *
 * Narrow on purpose: a fake implementation is a plain object, which is what makes
 * the recovery path testable without killing a real worker.
 */
export interface KernelWorkerLike {
  postMessage(message: unknown): void;
  terminate(): void;
  onmessage: ((event: MessageEvent) => void) | null;
  onmessageerror: ((event: MessageEvent) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
}

/** What a restart recovered, so the UI can report it rather than assume it. */
export interface RecoveryReport {
  /** `error`, `messageerror`, or `stall`. */
  reason: string;
  correlationId: string;
  /** Restarts this session, including this one. */
  restart: number;
  /** Journal operations submitted for replay. */
  attempted: number;
  replay: ReplayReport | null;
}

export interface KernelClientOptions {
  /** Worker factory. Defaults to the real module worker. */
  spawn?: () => KernelWorkerLike;
  /** Forces a tile transport. Defaults to the best this page can read. */
  transport?: TileTransportMode;
  /** Journal that recovery replays. Defaults to an in-memory journal. */
  journal?: CommandJournal;
  /** Where recovery reads snapshots from. Task 23 supplies the durable store. */
  snapshots?: SnapshotStore;
  /** Silence after which the worker is declared dead. `0` disables the watchdog. */
  heartbeatTimeoutMs?: number;
  /** Liveness poll period. Defaults to a third of the timeout. */
  watchdogIntervalMs?: number;
  /**
   * Debounce for the autosave snapshot after the last successful operation
   * (Requirement 14.5, 500–1000 ms). Defaults to 700 ms.
   */
  autosaveDelayMs?: number;
  now?: () => number;
}

type Unsubscribe = () => void;

export interface KernelClient {
  /** Resolves with the first worker's replay report once it is ready. */
  readonly ready: Promise<ReplayReport>;
  dispatch(command: Command): Promise<CommandResult>;
  dispatchBatch(commands: readonly Command[], label?: string): Promise<CommandResult>;
  undo(): Promise<HistoryCommandResult>;
  redo(): Promise<HistoryCommandResult>;
  /** A viewport tile of raw values (Requirement 1.4). */
  tile(range: Range): Promise<TileSnapshot>;
  /** A viewport tile resolved for painting: text, alignment, styles (R1.4, 6.7). */
  view(range: Range): Promise<ViewTile>;
  /** Sheet geometry, freeze, merges, used range, and the tab list (R1.6–1.8, 1.12). */
  sheetView(sheet: SheetId): Promise<SheetView>;
  /** One cell's formula-bar content (Requirement 1.9). */
  cellContent(at: Addr): Promise<CellContent>;
  /** The `Ctrl+Arrow` block edge from `at` (Requirements 3.2, 3.3). */
  blockEdge(at: Addr, dir: Direction): Promise<Addr>;
  /** The `Ctrl+End` target: the used-range corner (Requirement 3.5). */
  lastUsedCell(sheet: SheetId): Promise<Addr>;
  /** The next cell in scan order containing `term`, or `null` (Requirement 7.9). */
  find(scope: Range, term: string, from: Addr, opts?: FindOptions): Promise<Addr | null>;
  /** The selection aggregate, computed in the worker (Requirements 1.10, 13.9). */
  aggregate(range: Range, bounds?: Range | null): Promise<AggResult>;
  /**
   * A chart's series as they stand now (Requirements 9.1, 9.3).
   *
   * The redraw read: the Chart_Renderer's subscription notices a `changed` broadcast
   * touching a chart's source and asks this for fresh series. A read, so it neither
   * mutates the Workbook nor disturbs the history.
   */
  chart(id: ChartId): Promise<ChartSummary>;
  /**
   * A copied block's values, formulas, and styles (Requirement 12.6).
   *
   * A read, like `chart`: `Ctrl+C` mutates nothing, so it must not open a history
   * entry. The payload is assembled in the worker because formula sources and style
   * ids live there.
   */
  clip(range: Range): Promise<ClipPayload>;
  /** The worker's session diagnostics log as JSON (Requirement 16.5). */
  diagnostics(): Promise<string>;
  /** Round-trips a `ping`, resolving with the worker's wall clock. */
  ping(): Promise<number>;
  onChanged(cb: (event: ChangedEvent) => void): Unsubscribe;
  onProgress(cb: (event: ProgressEvent) => void): Unsubscribe;
  onRecovered(cb: (report: RecoveryReport) => void): Unsubscribe;
  /** Reports an autosave failure, so the UI can surface it and offer a retry. */
  onSnapshotError(cb: (error: Error) => void): Unsubscribe;
  /**
   * Captures a snapshot of the workbook immediately (cancelling any pending
   * autosave) and writes it to the snapshot store. Resolves once the snapshot is
   * durable. Used before unload and after an import, where the debounce would
   * leave a window.
   */
  flushSnapshot(): Promise<void>;
  /** Pauses debounced snapshots during large imports/bulk mutations. */
  setAutosaveSuspended(suspended: boolean): void;
  /** Transport tiles currently arrive in. */
  transport(): TileTransportMode;
  /** The journal a restart would replay. */
  journal(): CommandJournal;
  /** Restarts that have happened this session. */
  restarts(): number;
  dispose(): void;
}

/** A protocol-level failure, carrying the envelope that explains it. */
export class KernelUnavailableError extends Error {
  override readonly name = 'KernelUnavailableError';
  readonly envelope: ErrorEnvelope;

  constructor(envelope: ErrorEnvelope) {
    super(envelope.message);
    this.envelope = envelope;
  }
}

function defaultSpawn(): KernelWorkerLike {
  return new Worker(new URL('./worker.ts', import.meta.url), {
    type: 'module',
    name: 'kernel',
  }) as unknown as KernelWorkerLike;
}

let correlationSeq = 0;

/**
 * Quiet-period after the last successful operation before an autosave snapshot
 * is captured (Requirement 14.5: 500–1000 ms).
 */
export const AUTOSAVE_DELAY_MS = 700;

/**
 * Serialising a very large workbook can temporarily pause the worker event
 * loop, so heartbeats are not observable while the snapshot is being built.
 * Keep this grace period separate from the normal crash watchdog.
 */
export const SNAPSHOT_STALL_GRACE_MS = 5 * 60 * 1_000;

/**
 * A correlation id for a client-side failure.
 *
 * The worker mints its own with `newCorrelationId`; importing that here for the
 * sake of one string would pull the computation core into the main-thread bundle,
 * so the client mints ids of the same shape with a `client-` marker.
 */
function clientCorrelationId(): string {
  const crypto: unknown = globalThis.crypto;
  if (
    typeof crypto === 'object' &&
    crypto !== null &&
    typeof (crypto as Crypto).randomUUID === 'function'
  ) {
    return `client-${(crypto as Crypto).randomUUID()}`;
  }
  correlationSeq += 1;
  return `client-${Date.now().toString(36)}-${correlationSeq.toString(36)}`;
}

function unavailable(message: string, correlationId: string): ErrorEnvelope {
  return {
    correlationId,
    subsystem: 'command',
    kind: 'unhandled',
    message,
    cancelled: true,
    at: Date.now(),
  };
}

interface Pending {
  kind: ClientRequestKind;
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
}

export function createKernelClient(options: KernelClientOptions = {}): KernelClient {
  const spawn = options.spawn ?? defaultSpawn;
  const now = options.now ?? Date.now;
  const journal = options.journal ?? createMemoryJournal();
  const snapshots = options.snapshots ?? createNullSnapshotStore();
  const autosaveDelayMs = options.autosaveDelayMs ?? AUTOSAVE_DELAY_MS;
  const timeoutMs = options.heartbeatTimeoutMs ?? HEARTBEAT_TIMEOUT_MS;
  const watchdogIntervalMs =
    options.watchdogIntervalMs ?? Math.max(1, Math.floor(timeoutMs / 3));
  const wanted = options.transport ?? preferredTileTransport();

  let mode: TileTransportMode = wanted;
  let worker: KernelWorkerLike | null = null;
  let generation = 0;
  let nextId = 1;
  let restartCount = 0;
  let disposed = false;
  let lastSeen = now();
  let watchdog: ReturnType<typeof setInterval> | null = null;

  const pending = new Map<number, Pending>();
  const changedListeners = new Set<(event: ChangedEvent) => void>();
  const progressListeners = new Set<(event: ProgressEvent) => void>();
  const recoveredListeners = new Set<(report: RecoveryReport) => void>();
  const snapshotErrorListeners = new Set<(error: Error) => void>();
  let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  let snapshotInFlight = false;
  let snapshotStartedAt = 0;
  let autosaveSuspended = false;

  let settleReady: (report: ReplayReport) => void = () => undefined;
  let failReady: (error: Error) => void = () => undefined;
  const ready = new Promise<ReplayReport>((resolve, reject) => {
    settleReady = resolve;
    failReady = reject;
  });
  let readySettled = false;
  // A crash before the first `ready` rejects this promise. Callers that never await
  // it must not turn that into an unhandled rejection.
  void ready.catch(() => undefined);

  /**
   * The first worker's boot: load the journal, read the newest snapshot, and
   * start the worker with the replay. A request issued before this settles is
   * queued by {@link send} rather than rejected — the old client spawned its
   * worker synchronously at construction, and callers (tests, the ZIP exporter)
   * legitimately fire requests right after `createKernelClient`. Durability makes
   * the boot async (it must read IndexedDB first), so the queue keeps the old
   * call-now contract.
   */
  let initialBoot: Promise<void> | undefined;
  const runBoot = (): void => {
    initialBoot = (async (): Promise<void> => {
      const report = await rebuild();
      if (!readySettled) {
        readySettled = true;
        if (report === null) {
          failReady(
            new KernelUnavailableError(unavailable('kernel worker failed to start', clientCorrelationId())),
          );
        } else {
          settleReady(report);
        }
      }
    })();
  };
  runBoot();

  async function send<T>(request: ClientRequest): Promise<T> {
    if (disposed) {
      return Promise.reject(
        new KernelUnavailableError(unavailable('kernel client disposed', clientCorrelationId())),
      );
    }
    let target = worker;
    if (target === null && initialBoot !== undefined) {
      // Queue behind the first worker's boot: a call made before the snapshot
      // and journal were loaded must answer from the *restored* kernel, not an
      // empty one. Once the boot is over, a null worker means a restart is in
      // progress, which is rejected below exactly as before.
      await initialBoot;
      target = worker;
    }
    if (target === null) {
      return Promise.reject(
        new KernelUnavailableError(
          unavailable('kernel worker is restarting', clientCorrelationId()),
        ),
      );
    }
    return new Promise<T>((resolve, reject) => {
      pending.set(request.id, {
        kind: request.k,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
      target.postMessage(request);
    });
  }

  function receive(event: KernelEvent, from: number): void {
    if (from !== generation) return; // a message from a worker already buried
    lastSeen = now();

    if (!isReply(event)) {
      switch (event.k) {
        case 'changed':
          for (const cb of changedListeners) cb(event);
          return;
        case 'progress':
          for (const cb of progressListeners) cb(event);
          return;
        case 'heartbeat':
          return;
      }
    }

    const entry = pending.get(event.id);
    if (entry === undefined) return;
    switch (event.k) {
      case 'ready':
        pending.delete(event.id);
        entry.resolve(event.replay);
        return;
      case 'result':
      case 'history':
        pending.delete(event.id);
        entry.resolve(event.result);
        return;
      case 'tile':
        pending.delete(event.id);
        mode = event.tile.transport;
        entry.resolve(decodeTile(event.tile));
        return;
      case 'view':
        pending.delete(event.id);
        entry.resolve(event.tile);
        return;
      case 'sheetView':
        pending.delete(event.id);
        entry.resolve(event.view);
        return;
      case 'cell':
        pending.delete(event.id);
        entry.resolve(event.content);
        return;
      case 'addr':
      case 'found':
        pending.delete(event.id);
        entry.resolve(event.addr);
        return;
      case 'aggregate':
        pending.delete(event.id);
        entry.resolve(event.result);
        return;
      case 'chart':
        pending.delete(event.id);
        entry.resolve(event.summary);
        return;
      case 'clip':
        pending.delete(event.id);
        entry.resolve(event.payload);
        return;
      case 'diagnostics':
        pending.delete(event.id);
        entry.resolve(event.json);
        return;
      case 'pong':
        pending.delete(event.id);
        entry.resolve(event.at);
        return;
      case 'snapshotData':
        pending.delete(event.id);
        entry.resolve(event.bytes);
        return;
      case 'failed':
        // `init` is the one request that can produce a warning and then still
        // succeed: the host reports an unusable snapshot this way and replays the
        // journal from empty anyway. Keep waiting for `ready` in that case.
        if (entry.kind === 'init') return;
        pending.delete(event.id);
        entry.reject(new KernelUnavailableError(event.error));
        return;
      default:
        return;
    }
  }

  async function start(replay: readonly JournalOp[], snapshot: unknown): Promise<ReplayReport> {
    generation += 1;
    const mine = generation;
    const created = spawn();
    worker = created;
    lastSeen = now();
    created.onmessage = (event: MessageEvent): void => {
      receive(event.data as KernelEvent, mine);
    };
    created.onmessageerror = (): void => {
      crash('messageerror', mine);
    };
    created.onerror = (): void => {
      crash('error', mine);
    };
    const report = await send<ReplayReport>({
      k: 'init',
      id: nextId++,
      snapshot,
      replay,
    });
    return report;
  }

  /**
   * Rebuilds state in a fresh worker: hydrate the journal, read the newest
   * snapshot, and replay the operations recorded since it. This is the boot path
   * and the crash-recovery path alike — a refresh is just a boot that finds a
   * snapshot and a durable journal waiting for it.
   */
  async function rebuild(): Promise<ReplayReport | null> {
    try {
      await journal.load?.().catch((err) => console.warn('[persistence] journal load error:', err));
      const snapshot = await snapshots.latest().catch((err) => {
        console.warn('[persistence] snapshot load error:', err);
        return null;
      });
      const ops = journal.since(snapshot?.seq ?? 0).map((e) => e.op);
      console.debug(
        '[persistence] rebuild: snapshot=%s, journal ops to replay=%d',
        snapshot !== null ? `seq=${snapshot.seq}` : 'none',
        ops.length,
      );
      return await start(ops, snapshot?.payload ?? null);
    } catch (e) {
      console.warn('[persistence] rebuild fallback to fresh workbook:', e instanceof Error ? e.message : String(e));
      try {
        return await start([], null);
      } catch (err) {
        console.error('[persistence] rebuild fallback start failed:', err);
        return null;
      }
    }
  }

  /** Buries a dead worker and rebuilds its state in a fresh one. */
  function crash(reason: string, from: number): void {
    if (disposed || from !== generation) return;
    const correlationId = clientCorrelationId();
    const dead = worker;
    worker = null;
    if (dead !== null) {
      dead.onmessage = null;
      dead.onmessageerror = null;
      dead.onerror = null;
      try {
        dead.terminate();
      } catch {
        // A worker that already died refuses to be terminated. Nothing to do.
      }
    }

    const envelope = unavailable(
      `kernel worker stopped responding (${reason}); its state is being rebuilt from the journal`,
      correlationId,
    );
    for (const [, entry] of pending) entry.reject(new KernelUnavailableError(envelope));
    pending.clear();
    if (!readySettled) {
      readySettled = true;
      failReady(new KernelUnavailableError(envelope));
    }

    restartCount += 1;
    const restart = restartCount;
    void (async (): Promise<void> => {
      let attempted = 0;
      let replay: ReplayReport | null = null;
      const rebuildStart = async (): Promise<ReplayReport | null> => {
        await journal.load?.();
        const snapshot = await snapshots.latest();
        const ops = journal.since(snapshot?.seq ?? 0).map((e) => e.op);
        attempted = ops.length;
        return start(ops, snapshot?.payload ?? null);
      };
      try {
        replay = await rebuildStart();
      } catch {
        // The replacement worker failed too. `replay: null` is the report, and the
        // watchdog will try again if it ever does come alive.
        replay = null;
      }
      for (const cb of recoveredListeners) {
        cb({ reason, correlationId, restart, attempted, replay });
      }
      // Baseline the durable log against the rebuilt state.
      scheduleAutosave();
    })();
  }

  function check(): void {
    if (disposed || worker === null) return;
    const silentFor = now() - lastSeen;
    const snapshotGraceActive = snapshotInFlight && snapshotStartedAt > 0 &&
      now() - snapshotStartedAt < SNAPSHOT_STALL_GRACE_MS;
    if (silentFor > timeoutMs && !snapshotGraceActive) crash('stall', generation);
  }



  if (timeoutMs > 0 && watchdogIntervalMs > 0) {
    watchdog = setInterval(check, watchdogIntervalMs);
  }

  async function submit(request: ClientRequest, op: JournalOp): Promise<CommandResult> {
    const result = await send<CommandResult>(request);
    if (result.ok) {
      journal.append(op);
      scheduleAutosave();
    }
    return result;
  }

  /** Debounced autosave: one snapshot per quiet period (Requirement 14.5). */
  function scheduleAutosave(): void {
    if (autosaveSuspended) return;
    if (autosaveTimer !== null) clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(() => {
      autosaveTimer = null;
      void captureSnapshot();
    }, autosaveDelayMs);
  }

  /**
   * Asks the worker for a `.wbk` snapshot, stores it, and truncates the journal
   * up to the snapshot's position. Reports failures to the snapshot-error
   * listeners and schedules one automatic retry.
   */
  async function captureSnapshot(): Promise<void> {
    if (autosaveTimer !== null) {
      clearTimeout(autosaveTimer);
      autosaveTimer = null;
    }
    const target = worker;
    if (target === null || snapshotInFlight) return;
    // Guard: don't save a snapshot when no real operations have been journaled.
    // This prevents a fresh boot from writing an empty workbook over saved data.
    if (journal.head() === 0 && journal.size() === 0) {
      console.debug('[persistence] captureSnapshot skipped: no operations journaled yet');
      return;
    }
    snapshotInFlight = true;
    snapshotStartedAt = now();
    try {
      const bytes = await send<Uint8Array>({ k: 'snapshot', id: nextId++ });
      const seq = journal.head();
      await snapshots.put({ seq, payload: bytes });
      journal.truncate(seq);
      await journal.flush?.();
      console.debug('[persistence] snapshot captured and journal truncated at seq=%d', seq);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      console.warn('[persistence] snapshot capture failed:', error.message);
      for (const cb of snapshotErrorListeners) cb(error);
      if (autosaveTimer === null) {
        autosaveTimer = setTimeout(() => {
          autosaveTimer = null;
          void captureSnapshot();
        }, autosaveDelayMs);
      }
    } finally {
      snapshotInFlight = false;
      snapshotStartedAt = 0;
    }
  }

  async function flushSnapshot(): Promise<void> {
    if (autosaveTimer !== null) {
      clearTimeout(autosaveTimer);
      autosaveTimer = null;
    }
    // Wait for an in-flight autosave rather than racing it: the newest snapshot
    // wins either way, but the caller (pagehide, import commit) wants the write
    // it triggered to be the last one.
    while (snapshotInFlight) {
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
    await captureSnapshot();
  }

  function setAutosaveSuspended(suspended: boolean): void {
    autosaveSuspended = suspended;
    if (suspended && autosaveTimer !== null) {
      clearTimeout(autosaveTimer);
      autosaveTimer = null;
    } else if (!suspended && journal.head() > 0) {
      scheduleAutosave();
    }
  }

  return {
    ready,

    dispatch(command: Command): Promise<CommandResult> {
      return submit({ k: 'dispatch', id: nextId++, command }, { k: 'command', command });
    },

    dispatchBatch(commands: readonly Command[], label = 'Batch'): Promise<CommandResult> {
      const copy = [...commands];
      return submit(
        { k: 'dispatchBatch', id: nextId++, commands: copy, label },
        { k: 'batch', commands: copy, label },
      );
    },

    async undo(): Promise<HistoryCommandResult> {
      const result = await send<HistoryCommandResult>({ k: 'undo', id: nextId++ });
      // A no-op undo (empty history) is not an operation to replay.
      if (result.ok && result.result !== null) journal.append({ k: 'undo' });
      return result;
    },

    async redo(): Promise<HistoryCommandResult> {
      const result = await send<HistoryCommandResult>({ k: 'redo', id: nextId++ });
      if (result.ok && result.result !== null) journal.append({ k: 'redo' });
      return result;
    },

    tile(range: Range): Promise<TileSnapshot> {
      return send<TileSnapshot>({
        k: 'tile',
        id: nextId++,
        range,
        // Re-checked per request: isolation is a property of the page's headers,
        // and a forced mode must never ask for buffers this realm cannot read.
        transport: wanted === 'shared' ? preferredTileTransport() : 'copy',
      });
    },

    view(range: Range): Promise<ViewTile> {
      return send<ViewTile>({ k: 'view', id: nextId++, range });
    },

    sheetView(sheet: SheetId): Promise<SheetView> {
      return send<SheetView>({ k: 'sheetView', id: nextId++, sheet });
    },

    cellContent(at: Addr): Promise<CellContent> {
      return send<CellContent>({ k: 'cell', id: nextId++, addr: at });
    },

    blockEdge(at: Addr, dir: Direction): Promise<Addr> {
      return send<Addr>({ k: 'blockEdge', id: nextId++, from: at, dir });
    },

    lastUsedCell(sheet: SheetId): Promise<Addr> {
      return send<Addr>({ k: 'lastUsed', id: nextId++, sheet });
    },

    find(scope: Range, term: string, from: Addr, opts: FindOptions = {}): Promise<Addr | null> {
      return send<Addr | null>({ k: 'find', id: nextId++, scope, term, from, opts });
    },

    aggregate(range: Range, bounds: Range | null = null): Promise<AggResult> {
      return send<AggResult>({ k: 'aggregate', id: nextId++, range, bounds });
    },

    chart(id: ChartId): Promise<ChartSummary> {
      return send<ChartSummary>({ k: 'chart', id: nextId++, chart: id });
    },

    clip(range: Range): Promise<ClipPayload> {
      return send<ClipPayload>({ k: 'clip', id: nextId++, range });
    },

    diagnostics(): Promise<string> {
      return send<string>({ k: 'diagnostics', id: nextId++ });
    },

    ping(): Promise<number> {
      return send<number>({ k: 'ping', id: nextId++ });
    },

    onChanged(cb): Unsubscribe {
      changedListeners.add(cb);
      return () => {
        changedListeners.delete(cb);
      };
    },

    onProgress(cb): Unsubscribe {
      progressListeners.add(cb);
      return () => {
        progressListeners.delete(cb);
      };
    },

    onRecovered(cb): Unsubscribe {
      recoveredListeners.add(cb);
      return () => {
        recoveredListeners.delete(cb);
      };
    },

    onSnapshotError(cb): Unsubscribe {
      snapshotErrorListeners.add(cb);
      return () => {
        snapshotErrorListeners.delete(cb);
      };
    },

    flushSnapshot,

    setAutosaveSuspended,

    transport(): TileTransportMode {
      return mode;
    },

    journal(): CommandJournal {
      return journal;
    },

    restarts(): number {
      return restartCount;
    },

    dispose(): void {
      disposed = true;
      if (watchdog !== null) {
        clearInterval(watchdog);
        watchdog = null;
      }
      const envelope = unavailable('kernel client disposed', clientCorrelationId());
      for (const [, entry] of pending) entry.reject(new KernelUnavailableError(envelope));
      pending.clear();
      const dead = worker;
      worker = null;
      if (dead !== null) {
        dead.onmessage = null;
        dead.onmessageerror = null;
        dead.onerror = null;
        dead.terminate();
      }
    },
  };
}

/** The protocol version this client speaks, for a host-side compatibility check. */
export const CLIENT_PROTOCOL_VERSION = KERNEL_PROTOCOL_VERSION;
